import { tokenLimitSchema } from '../../server/src/modules/gateway/contracts';
import type { ChatRequest } from './providers/types';
import type { Preset } from './types';

/** Match offline Moments' three-character input estimate, independently of output tokens. */
export function applyRoleContextBudget<T extends ChatRequest>(request: T, preset: Pick<Preset, 'modelConfig'> | null | undefined): T {
  if (preset?.modelConfig === undefined) return request;
  const budget = tokenLimitSchema.parse(request.settings.maxContext) * 3;
  const fixedSize = JSON.stringify(request.tools ?? []).length + (request.settings.systemPrompt?.length ?? 0) + 128;
  const size = (messages: ChatRequest['messages']) => fixedSize + JSON.stringify(messages.map(message => ({
    role: message.role, content: message.content, attachments: message.attachments, images: message.images,
    toolCallId: message.toolCallId, toolCalls: message.toolCalls,
  }))).length;
  const system = request.messages.filter(message => message.role === 'system');
  if (size(system) > budget) throw new Error('角色指令和工具超过角色模型的最大上下文，请提高最大上下文或缩短角色设定');

  // Remove whole turns, including every tool call/result, so a retained turn is never orphaned.
  const turns: number[][] = [];
  const pendingCalls = new Set<string>();
  for (const [index, message] of request.messages.entries()) {
    if (message.role === 'system') continue;
    if (turns.length === 0 || (message.role === 'user' && pendingCalls.size === 0)) turns.push([]);
    turns.at(-1)!.push(index);
    for (const call of message.toolCalls ?? []) pendingCalls.add(call.id);
    if (message.role === 'tool' && message.toolCallId) pendingCalls.delete(message.toolCallId);
  }
  const removed = new Set<number>();
  let messages = [...request.messages];
  for (const turn of turns.slice(0, -1)) {
    if (size(messages) <= budget) break;
    for (const index of turn) removed.add(index);
    messages = request.messages.filter((_, index) => !removed.has(index));
  }
  if (size(messages) > budget) throw new Error('最新消息及附件超过角色模型的最大上下文，请提高最大上下文或缩短当前消息');
  return { ...request, messages };
}
